import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import type { LabResult, LabValueFlag } from '../../types/laboklin';

/**
 * The measured values, with each flagged row linked to the sentence that uses it.
 *
 * A separate component from the drawer's TestResultsTable rather than a variant of
 * it. This page is a different reading: the interpretation sits beside the table
 * instead of after it, so a row's job here is to hand the reader across to the line
 * that argues from it, and the material column that matters in the drawer is stated
 * once above instead. Both render the same server payload and the same
 * server-computed flags, so they cannot disagree about a value.
 */

const FLAG_COLOR: Record<LabValueFlag, string | undefined> = {
  high: '#d32f2f',
  low: '#1a56b3',
  normal: undefined,
  unknown: undefined,
};

interface ResultsTableProps {
  results: LabResult[];
  /** Analyte id -> the section title that reasons from it. */
  linkedSections: Map<string, string>;
  onTagClick: (analyteId: string) => void;
  highlightedAnalyteIds?: Set<string>;
}

export const ResultsTable: React.FC<ResultsTableProps> = ({
  results,
  linkedSections,
  onTagClick,
  highlightedAnalyteIds,
}) => {
  if (results.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
        This report contains no measured values.
      </Typography>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Parameter</TableCell>
            <TableCell align="right">Value</TableCell>
            <TableCell>Unit</TableCell>
            <TableCell>Reference</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {results.map((r) => {
            const color = FLAG_COLOR[r.flag];
            const isAbnormal = r.flag === 'high' || r.flag === 'low';
            const sectionTitle = linkedSections.get(r.analyteId);
            const isHighlighted = highlightedAnalyteIds?.has(r.analyteId) ?? false;

            return (
              <TableRow
                key={`${r.analyteId}-${r.rawName}`}
                sx={{
                  borderLeft: color ? `3px solid ${color}` : '3px solid transparent',
                  backgroundColor: isHighlighted
                    ? 'rgba(229, 30, 30, 0.06)'
                    : isAbnormal
                      ? 'rgba(0,0,0,0.02)'
                      : undefined,
                  transition: 'background-color 200ms',
                }}
              >
                <TableCell>
                  {/* rawName preserves Laboklin's own "*" markers. */}
                  {r.rawName || r.name}
                  {sectionTitle && (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => onTagClick(r.analyteId)}
                      title={`Jump to "${sectionTitle}"`}
                      sx={{
                        ml: 1,
                        px: 0.75,
                        py: 0.125,
                        fontSize: 11,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        borderRadius: '3px',
                        border: '1px solid #c7dccb',
                        bgcolor: '#eef3ee',
                        color: '#1f5a2a',
                        '&:hover': { bgcolor: '#dfe9df' },
                      }}
                    >
                      {sectionTitle}
                    </Box>
                  )}
                  {r.valueText && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      {r.valueText}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Box
                    component="span"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: isAbnormal ? 700 : 400,
                      color: color ?? 'inherit',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {r.rawValue}
                    {isAbnormal && r.foldBeyondLimit !== null && (
                      <Typography component="span" variant="caption" sx={{ ml: 0.75, color, fontWeight: 400 }}>
                        {r.foldBeyondLimit}× {r.flag === 'high' ? 'over' : 'under'}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{r.unit ?? ''}</TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{r.referenceRange}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default ResultsTable;
